import React, { useState, useEffect } from 'react';
import { useFirebase } from '../FirebaseProvider';
import { 
  X, 
  LayoutGrid, 
  FileText, 
  BookOpen, 
  BarChart3, 
  ArrowLeft,
  Plus,
  RefreshCw,
  AlertCircle,
  Database
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { AccountingAccount, JournalEntry, Account, PlannedConcept } from '../../types';
import { 
  subscribeToAccountingAccounts, 
  subscribeToAccountingEntries, 
  seedChartOfAccounts, 
  deleteAccountingAccount, 
  deleteJournalEntry, 
  generateInitialBalancesEntry,
  generateJournalEntriesFromDocuments 
} from '../../services/accountingService';
import { getPlannedConcepts } from '../../services/planningService';
import { ChartView } from './ChartView';
import { JournalView } from './JournalView';
import { LedgerView } from './LedgerView';
import { ReportsView } from './ReportsView';
import { AccountingProcessesView } from './AccountingProcessesView';

interface AccountingDashboardProps {
  onBack: () => void;
  financeAccounts: Account[];
}

export const AccountingDashboard: React.FC<AccountingDashboardProps> = ({ onBack, financeAccounts }) => {
  const { effectiveUserId } = useFirebase();
  const [activeTab, setActiveTab] = useState<'CHART' | 'JOURNAL' | 'LEDGER' | 'REPORTS' | 'PROCESSES'>('PROCESSES');
  const [accounts, setAccounts] = useState<AccountingAccount[]>([]);
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [concepts, setConcepts] = useState<PlannedConcept[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    if (effectiveUserId) {
      setLoading(true);
      const unsubAcc = subscribeToAccountingAccounts(effectiveUserId, (data) => {
        setAccounts(data);
        if (loading) setLoading(false);
      });
      const unsubEntries = subscribeToAccountingEntries(effectiveUserId, (data) => {
        setEntries(data);
      });
      const unsubConcepts = getPlannedConcepts(effectiveUserId, (data) => {
        setConcepts(data);
      });
      return () => {
        unsubAcc();
        unsubEntries();
        unsubConcepts();
      };
    }
  }, [effectiveUserId]);

  const handleSeed = async () => {
    if (!effectiveUserId) return;
    setSeeding(true);
    try {
      await seedChartOfAccounts(effectiveUserId, financeAccounts as any, concepts);
    } catch (error) {
      console.error("Error seeding accounts:", error);
      alert("Error al cargar la plantilla.");
    } finally {
      setSeeding(false);
    }
  };

  const handleFullReset = async () => {
    if (!effectiveUserId) return;
    
    setIsResetting(true);
    try {
      // 1. Delete all entries sequentially
      for (const entry of entries) {
        await deleteJournalEntry(effectiveUserId, entry.id);
      }
      
      // 2. Delete all accounts
      // No order needed for Firestore delete
      for (const acc of accounts) {
        await deleteAccountingAccount(effectiveUserId, acc.id);
      }
      
      // 3. Re-seed with corrected logic and integration
      await seedChartOfAccounts(effectiveUserId, financeAccounts as any, concepts);
      
      // Force a small delay to ensure snapshot updates haven't confused the UI
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log("Módulo reiniciado con éxito.");
    } catch (error) {
      console.error("Reset error:", error);
      alert("Error al reiniciar. Intenta recargar la página.");
    } finally {
      setIsResetting(false);
    }
  };

  const handleGenerateBalances = async () => {
    if (!effectiveUserId) return;
    try {
      await generateInitialBalancesEntry(effectiveUserId, accounts, financeAccounts as any);
      alert('Asiento de apertura generado correctamente.');
      setActiveTab('JOURNAL'); // Redirigir al libro diario tras generarlo
    } catch (error: any) {
      throw error; // Rethrow to be caught by the view
    }
  };

  const handleSettleDocuments = async () => {
    if (!effectiveUserId) return;
    try {
      await generateJournalEntriesFromDocuments(effectiveUserId, accounts, financeAccounts as any);
      alert('Documentos asentados correctamente en el libro diario.');
      setActiveTab('JOURNAL');
    } catch (error: any) {
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <RefreshCw className="animate-spin text-zinc-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack}
            className="p-2 hover:bg-zinc-800 rounded-xl transition-colors text-zinc-400 hover:text-white"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              Módulo Contable
              <span className="text-[10px] bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-widest border border-indigo-500/30">
                Beta
              </span>
            </h1>
            <p className="text-sm text-zinc-500">Gestión contable financiera por partida doble.</p>
          </div>
        </div>
      </header>

      {accounts.length === 0 && activeTab !== 'PROCESSES' ? (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-12 text-center flex flex-col items-center max-w-2xl mx-auto my-12">
          <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mb-6 text-zinc-600">
            <LayoutGrid size={32} />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Tu Plan de Cuentas está vacío</h2>
          <p className="text-zinc-500 mb-8">
            Para comenzar a usar el módulo contable, necesitas Inicializar el Módulo desde la pestaña de procesos.
          </p>
          <button 
            onClick={() => setActiveTab('PROCESSES')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-2xl font-bold transition-all"
          >
            Ir a Procesos Contables
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Navigation Tabs */}
          <nav className="flex gap-1 bg-zinc-900 p-1.5 rounded-2xl border border-zinc-800 overflow-x-auto scrolbar-hide">
            <button 
              onClick={() => setActiveTab('PROCESSES')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'PROCESSES' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <Database size={18} />
              Procesos
            </button>
            <button 
              onClick={() => setActiveTab('CHART')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'CHART' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <LayoutGrid size={18} />
              Plan de Cuentas
            </button>
            <button 
              onClick={() => setActiveTab('JOURNAL')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'JOURNAL' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <FileText size={18} />
              Libro Diario
            </button>
            <button 
              onClick={() => setActiveTab('LEDGER')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'LEDGER' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <BookOpen size={18} />
              Libro Mayor
            </button>
            <button 
              onClick={() => setActiveTab('REPORTS')}
              className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap ${
                activeTab === 'REPORTS' ? 'bg-zinc-800 text-white' : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              <BarChart3 size={18} />
              Estados Financieros
            </button>
          </nav>

          <main>
            <AnimatePresence mode="wait">
              {activeTab === 'PROCESSES' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <AccountingProcessesView 
                    userId={effectiveUserId!}
                    accounts={accounts}
                    financeAccounts={financeAccounts as any}
                    onInitialize={handleFullReset}
                    onGenerateInitialBalances={handleGenerateBalances}
                    onSettleDocuments={handleSettleDocuments}
                    isInitializing={isResetting || seeding}
                  />
                </motion.div>
              )}
              {activeTab === 'CHART' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <ChartView 
                    accounts={accounts} 
                    userId={effectiveUserId!} 
                    financeAccounts={financeAccounts as any} 
                    plannedConcepts={concepts}
                  />
                </motion.div>
              )}
              {activeTab === 'JOURNAL' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <JournalView entries={entries} accounts={accounts} userId={effectiveUserId!} />
                </motion.div>
              )}
              {activeTab === 'LEDGER' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <LedgerView entries={entries} accounts={accounts} />
                </motion.div>
              )}
              {activeTab === 'REPORTS' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <ReportsView entries={entries} accounts={accounts} />
                </motion.div>
              )}
            </AnimatePresence>
          </main>
        </div>
      )}
    </div>
  );
};
